import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { useContext, useMemo } from "react";
import { useRescoreBenefitClustersMutation } from "store/queries/benefitComparison";
import {
  IRescoreClusterKey,
  IRescoreClustersRun,
  IWorklistRow,
} from "types/BenefitComparison/BenefitComparison";

import {
  RERUN_ALL_ALREADY_RUNNING_TEXT,
  RERUN_ALL_CONFIRM_HEADER,
  RERUN_ALL_LABEL,
  RERUN_ALL_NOTHING_TOOLTIP,
  RERUN_ALL_RUNNING_TOOLTIP,
  rerunAllConfirmText,
  rerunAllStartedText,
} from "./benefitComparisonWording";

/**
 * The cluster keys "Rerun all clusters" sends: one per row the table lists, on EVERY page.
 *
 * 🚨 `rows` IS THE TABLE AFTER EVERY FILTER — account and payer (applied by the API), "Flagged" and
 * "Leave out hidden clusters" (also the API), and "Engine disagrees with", the regressed link and
 * "Benefit" (applied in the browser). Re-running anything else would re-run clusters the person
 * filtered away.
 *
 * A NOT_CONFIGURED row (`benefit === null`) is a configuration-gap rollup with no cluster key, so it
 * is left out — the fix for it is "Configure", not a re-run.
 */
export const rerunnableClusterKeys = (
  rows: IWorklistRow[]
): IRescoreClusterKey[] =>
  rows.flatMap((row) =>
    row.benefit === null
      ? []
      : [
          {
            accountId: row.accountId,
            payerId: row.payerId === null ? "none" : row.payerId,
            benefit: row.benefit,
            readFrom: row.readFrom,
            ...(row.planName === null
              ? { planNameMissing: true as const }
              : { planName: row.planName }),
          },
        ]
  );

/**
 * "Rerun all clusters" — above the worklist table, on the right.
 *
 * 🚨 THE API ANSWERS AT ONCE AND WORKS IN THE BACKGROUND. The toast says what was queued; the
 * freshness banner (`BenefitWorklistFreshness`) then shows the run from `freshness.rescoring` and
 * polls until the worklist has been rebuilt. While that field is set the button stays disabled, so a
 * second press cannot queue a second run (the API's 409 is the backstop).
 */
export function BenefitWorklistRerunAll({
  rows,
  rescoring,
}: {
  rows: IWorklistRow[];
  rescoring: IRescoreClustersRun | null | undefined;
}) {
  const toast = useContext(ToastContext);
  const [rescoreClusters, { isLoading }] = useRescoreBenefitClustersMutation();
  const clusters = useMemo(() => rerunnableClusterKeys(rows), [rows]);

  const running = Boolean(rescoring) || isLoading;
  const disabled = running || clusters.length === 0;
  let tooltip: string | undefined;
  if (running) tooltip = RERUN_ALL_RUNNING_TOOLTIP;
  else if (clusters.length === 0) tooltip = RERUN_ALL_NOTHING_TOOLTIP;

  const run = async () => {
    try {
      const { data } = await rescoreClusters({ clusters }).unwrap();
      toast?.current?.show({
        severity: "info",
        summary: RERUN_ALL_LABEL,
        detail: rerunAllStartedText(data),
      });
    } catch (error: any) {
      toast?.current?.show(
        error?.status === 409
          ? {
              severity: "warn",
              summary: RERUN_ALL_LABEL,
              detail: RERUN_ALL_ALREADY_RUNNING_TEXT,
            }
          : {
              severity: "error",
              summary: "Not re-run",
              detail:
                error?.data?.message ||
                "The clusters could not be re-run. Nothing was changed.",
            }
      );
    }
  };

  const confirm = () =>
    confirmDialog({
      header: RERUN_ALL_CONFIRM_HEADER,
      style: { width: "min(520px, 95vw)" },
      message: rerunAllConfirmText(clusters.length),
      icon: "pi pi-refresh",
      acceptLabel: RERUN_ALL_LABEL,
      rejectLabel: "Cancel",
      accept: run,
    });

  return (
    <Button
      type="button"
      label={RERUN_ALL_LABEL}
      icon="pi pi-refresh"
      className="p-button-outlined p-button-sm"
      disabled={disabled}
      loading={isLoading}
      tooltip={tooltip}
      tooltipOptions={{ showOnDisabled: true, position: "top" }}
      data-testid="benefitWorklist.rerunAll"
      onClick={confirm}
    />
  );
}

export default BenefitWorklistRerunAll;
